import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

const ProductSalesTable = () => {
    const [products, setProducts] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [loading, setLoading] = useState(true);

    const [limit, setLimit] = useState(10);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [productOptions, setProductOptions] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productSearch, setProductSearch] = useState("");

    // Type-to-search for a specific product, debounced.
    useEffect(() => {
        if (!productSearch) { setProductOptions([]); return; }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.get('/api/v1/products', { params: { keyword: productSearch } });
                setProductOptions(data.products || []);
            } catch (error) {
                console.error('Failed to search products', error);
            }
        }, 400);
        return () => clearTimeout(timeout);
    }, [productSearch]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedProduct) {
                params.productId = selectedProduct._id;
            } else {
                params.limit = limit;
            }
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;

            const { data } = await axios.get('/api/v1/admin/dashboard/product-sales', { params });
            setProducts(data.products);
            setTotalRevenue(data.totalRevenue);
        } catch (error) {
            console.error('Failed to load product sales', error);
        } finally {
            setLoading(false);
        }
    }, [selectedProduct, limit, fromDate, toDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div className="bg-white rounded-xl shadow-lg p-5 flex flex-col gap-4">
            <div className="flex flex-wrap justify-between items-center gap-3">
                <h2 className="font-medium text-lg">Sales of Products</h2>

                <div className="flex flex-wrap items-center gap-2">
                    <Autocomplete
                        size="small"
                        sx={{ width: 220 }}
                        options={productOptions}
                        getOptionLabel={(opt) => opt.name || ""}
                        value={selectedProduct}
                        onChange={(e, newValue) => setSelectedProduct(newValue)}
                        onInputChange={(e, newInput) => setProductSearch(newInput)}
                        renderInput={(params) => <TextField {...params} label="Specific product (optional)" placeholder="Search..." />}
                    />

                    {!selectedProduct && (
                        <TextField
                            select
                            size="small"
                            label="Show"
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            sx={{ minWidth: 110 }}
                        >
                            <MenuItem value={10}>Top 10</MenuItem>
                            <MenuItem value={20}>Top 20</MenuItem>
                            <MenuItem value={30}>Top 30</MenuItem>
                        </TextField>
                    )}

                    <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />

                    {(selectedProduct || fromDate || toDate) && (
                        <button
                            onClick={() => { setSelectedProduct(null); setFromDate(""); setToDate(""); }}
                            className="text-sm text-gray-500 px-2"
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-gray-50 rounded-lg px-4 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                    {selectedProduct ? `Total sales for "${selectedProduct.name}"` : fromDate || toDate ? "Total sales in range" : "Total sales (all time)"}
                </span>
                <span className="text-xl font-semibold text-primary-green">HK${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>

            {loading ? (
                <p className="text-sm text-gray-400">Loading...</p>
            ) : products.length === 0 ? (
                <p className="text-sm text-gray-400">No sales data for this selection.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-2">Product</th>
                                <th className="py-2 pr-2 text-right">Units Sold</th>
                                <th className="py-2 text-right">Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p, i) => (
                                <tr key={p._id || i} className="border-b last:border-0">
                                    <td className="py-2 pr-2 flex items-center gap-2">
                                        {p.image && <img src={p.image} alt={p.name} className="w-8 h-8 object-contain rounded" />}
                                        <span className="truncate max-w-xs">{p.name}</span>
                                    </td>
                                    <td className="py-2 pr-2 text-right">{p.unitsSold}</td>
                                    <td className="py-2 text-right font-medium">HK${p.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ProductSalesTable;